import { z } from 'zod'
import {
  DesktopModuleKind,
  type DesktopAppSetting,
  type DesktopModule,
  type DesktopModuleKDS
} from '../../models/AppSetting'
import { mapDTOToPeripheral, PeripheralDTO } from '../../../peripherals/gateways/DTOs/PeripheralDTO'
import {
  mapDTOToPrintPointRedirect,
  mapPrintPointRedirectToDTO,
  PrintPointRedirectDTO
} from '../../../peripherals/gateways/DTOs/PrintPointRedirectDTO'
import { mapDTOToServerApp, ServerAppDTO } from './ServerAppDTO'
import { mapDTOToVpnSetting, VpnSettingDTO } from './VpnSettingDTO'
import { mapDTOToSlimModel, SlimModelDTO } from '~/domain/shared/slim/gateways/DTOs/SlimModelDTO'

const DesktopKdsSettingDTO = z.object({
  kds_id: z.number().nullish(),
  kds: SlimModelDTO.nullish()
})

const DesktopModuleBaseDTO = z.object({
  kind: z.nativeEnum(DesktopModuleKind)
})

const DesktopModuleKDSDTO = DesktopModuleBaseDTO.extend({
  kind: z.literal(DesktopModuleKind.KDS),
  settings: DesktopKdsSettingDTO
})

const DesktopModuleDTO = z.discriminatedUnion('kind', [
  DesktopModuleBaseDTO.extend({ kind: z.literal(DesktopModuleKind.PasswordPanel) }),
  DesktopModuleBaseDTO.extend({ kind: z.literal(DesktopModuleKind.Cashier) }),
  DesktopModuleBaseDTO.extend({ kind: z.literal(DesktopModuleKind.CustomerService) }),
  DesktopModuleKDSDTO
])

const DesktopPeripheralsSettingDTO = z.object({
  sat_id: z.number().nullish(),
  sat: PeripheralDTO.nullish(),
  print_point_id: z.number().nullish(),
  print_point: PeripheralDTO.nullish(),
  scale_id: z.number().nullish(),
  scale: PeripheralDTO.nullish()
})

export const DesktopAppSettingDTO = z.object({
  server_id: z.number().nullish(),
  server: ServerAppDTO.nullish(),
  modules: z.array(DesktopModuleDTO),
  print_point_redirects: z.array(PrintPointRedirectDTO).nullish(),
  peripherals: DesktopPeripheralsSettingDTO.nullish(),
  vpn: VpnSettingDTO.nullish()
})

export type DesktopAppSettingDTO = z.infer<typeof DesktopAppSettingDTO>

export function mapDesktopAppSettingToDTO (model: DesktopAppSetting): DesktopAppSettingDTO {
  return {
    server_id: model.server?.id,
    modules: model.modules.map((module) => {
      if (module.kind === DesktopModuleKind.KDS) {
        const kdsModule = module as DesktopModuleKDS
        return {
          kind: module.kind,
          settings: {
            kds_id: kdsModule.settings.kds.id
          }
        }
      }
      return {
        kind: module.kind
      }
    }),
    print_point_redirects: model.redirectEnabled
      ? model.printPointRedirects?.map(redirect => mapPrintPointRedirectToDTO(redirect))
      : null,
    peripherals: model.peripherals
      ? {
          sat_id: model.peripherals.sat?.id,
          print_point_id: model.peripherals.printPoint?.id,
          scale_id: model.peripherals.scale?.id
        }
      : undefined
  }
}

export function mapDTOToDesktopAppSetting (dto: DesktopAppSettingDTO): DesktopAppSetting {
  return {
    server: dto.server ? mapDTOToServerApp(dto.server) : undefined,
    modules: dto.modules.map((module) => {
      if (module.kind === DesktopModuleKind.KDS) {
        const kdsModuleDTO = module as z.infer<typeof DesktopModuleKDSDTO>
        return {
          kind: module.kind,
          settings: {
            kds: kdsModuleDTO.settings.kds ? mapDTOToSlimModel(kdsModuleDTO.settings.kds) : undefined
          }
        } as DesktopModuleKDS
      }
      return {
        kind: module.kind
      } as DesktopModule
    }),
    printPointRedirects: dto.print_point_redirects?.map(redirect => mapDTOToPrintPointRedirect(redirect)) ?? undefined,
    redirectEnabled: (dto.print_point_redirects?.length ?? 0) > 0,
    peripherals: dto.peripherals
      ? {
          sat: dto.peripherals.sat ? mapDTOToPeripheral(dto.peripherals.sat) : undefined,
          printPoint: dto.peripherals.print_point ? mapDTOToPeripheral(dto.peripherals.print_point) : undefined,
          scale: dto.peripherals.scale ? mapDTOToPeripheral(dto.peripherals.scale) : undefined
        }
      : undefined,
    vpn: dto.vpn ? mapDTOToVpnSetting(dto.vpn) : undefined
  }
}
